import apiService from './api';

// Dummy data for development
const DUMMY_IMAGES = [
  {
    id: '1',
    club_id: 'club_123',
    title: 'Team Victory 2024',
    description: 'Our team celebrating the championship victory at the final match',
    image_url: 'https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=800&h=600&fit=crop',
    display_order: 0,
    active: true,
    created_at: '2024-01-15T10:30:00Z',
    created_by: 'user_1'
  },
  {
    id: '2',
    club_id: 'club_123',
    title: 'Youth Training Session',
    description: 'Coach Mike leading the U12 team through advanced dribbling techniques',
    image_url: 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=800&h=600&fit=crop',
    display_order: 1,
    active: true,
    created_at: '2024-01-18T14:00:00Z',
    created_by: 'user_2'
  },
  {
    id: '3',
    club_id: 'club_123',
    title: 'Home Stadium',
    description: 'Our beautiful home ground where all the magic happens',
    image_url: 'https://images.unsplash.com/photo-1556056504-5c7696c4c28d?w=800&h=600&fit=crop',
    display_order: 2,
    active: true,
    created_at: '2024-01-20T09:15:00Z',
    created_by: 'user_1'
  },
  {
    id: '4',
    club_id: 'club_123',
    title: 'Pre-Match Warm Up',
    description: 'Players getting ready before the big game',
    image_url: 'https://images.unsplash.com/photo-1551958219-acbc608c6377?w=800&h=600&fit=crop',
    display_order: 3,
    active: true,
    created_at: '2024-01-22T16:45:00Z',
    created_by: 'user_3'
  },
  {
    id: '5',
    club_id: 'club_123',
    title: 'Goal Celebration',
    description: 'The moment we scored the winning goal in overtime',
    image_url: 'https://images.unsplash.com/photo-1522778119026-d647f0596c20?w=800&h=600&fit=crop',
    display_order: 4,
    active: true,
    created_at: '2024-01-25T19:30:00Z',
    created_by: 'user_2'
  },
  {
    id: '6',
    club_id: 'club_123',
    title: 'Team Photo Day',
    description: '',
    image_url: 'https://images.unsplash.com/photo-1509027572446-af8401acfdc3?w=800&h=600&fit=crop',
    display_order: 5,
    active: false,
    created_at: '2024-01-28T11:00:00Z',
    created_by: 'user_1'
  }
];

const DUMMY_SETTINGS = {
  club_id: 'club_123',
  carousel_interval: 5000, // milliseconds
  display_strategy: 'ordered', // 'ordered' or 'random'
  auto_advance: true,
  transition_effect: 'slide' // 'slide' or 'fade'
};

const galleryApi = {
  /**
   * Get all gallery images for the current club
   * @param {boolean} activeOnly - Filter for active images only
   * @returns {Promise<Array>} Array of gallery images
   */
  getImages: async (activeOnly = false) => {
    try {
      const response = await apiService.get(`/api/club-gallery/images?active_only=${activeOnly}`);
      // Backend returns { images: [...], total: N, active_count: N } directly
      // Wrap it in data property for consistency
      return { data: response };
    } catch (error) {
      console.warn('Gallery API unavailable, using dummy data:', error);
      // Fallback to dummy data
      return new Promise((resolve) => {
        setTimeout(() => {
          const filtered = activeOnly
            ? DUMMY_IMAGES.filter(img => img.active)
            : DUMMY_IMAGES;
          resolve({ data: { images: filtered } });
        }, 500);
      });
    }
  },

  /**
   * Get carousel data with settings
   * @returns {Promise<Object>} Carousel images and settings
   */
  getCarouselData: async () => {
    try {
      const response = await apiService.get('/api/public/gallery');
      // Backend returns { images: [...], settings: {...} } directly
      // Wrap it in data property for consistency with frontend expectations
      return { data: response };
    } catch (error) {
      console.warn('Gallery API unavailable, using dummy data:', error);
      // Fallback to dummy data
      return new Promise((resolve) => {
        setTimeout(() => {
          const activeImages = DUMMY_IMAGES.filter(img => img.active);
          const images = DUMMY_SETTINGS.display_strategy === 'random'
            ? [...activeImages].sort(() => Math.random() - 0.5)
            : activeImages.sort((a, b) => a.display_order - b.display_order);

          resolve({
            data: {
              images,
              settings: DUMMY_SETTINGS
            }
          });
        }, 500);
      });
    }
  },

  /**
   * Upload a new gallery image
   * @param {FormData} formData - Form data containing file, title, description, etc.
   * @returns {Promise<Object>} Created image data
   */
  uploadImage: async (formData) => {
    try {
      // Use fetch directly for FormData uploads
      const token = localStorage.getItem('authToken');
      const baseURL = process.env.REACT_APP_API_URL || '';
      const url = `${baseURL}/api/club-gallery/images`;

      const fetchOptions = {
        method: 'POST',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
          // Don't set Content-Type for FormData - browser will set it with boundary
        },
        body: formData
      };

      const fetchResponse = await fetch(url, fetchOptions);

      if (!fetchResponse.ok) {
        const errorData = await fetchResponse.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${fetchResponse.status}`);
      }

      const response = await fetchResponse.json();
      // Backend returns created image directly
      // Wrap it in data property for consistency
      return { data: response };
    } catch (error) {
      console.error('Failed to upload image:', error);
      throw error;
    }
  },

  /**
   * Update an existing gallery image
   * @param {string} imageId - Image ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated image data
   */
  updateImage: async (imageId, updates) => {
    try {
      const response = await apiService.put(`/api/club-gallery/images/${imageId}`, updates);
      // Backend returns updated image directly
      // Wrap it in data property for consistency
      return { data: response };
    } catch (error) {
      console.error('Failed to update image:', error);
      throw error;
    }
  },

  /**
   * Delete a gallery image
   * @param {string} imageId - Image ID
   * @returns {Promise<Object>} Success status
   */
  deleteImage: async (imageId) => {
    try {
      return await apiService.delete(`/api/club-gallery/images/${imageId}`);
    } catch (error) {
      console.error('Failed to delete image:', error);
      throw error;
    }
  },

  /**
   * Toggle active status of an image
   * @param {string} imageId - Image ID
   * @returns {Promise<Object>} Updated image data
   */
  toggleActive: async (imageId) => {
    try {
      const response = await apiService.put(`/api/club-gallery/images/${imageId}/toggle-active`);
      // Backend returns updated image directly
      // Wrap it in data property for consistency
      return { data: response };
    } catch (error) {
      console.error('Failed to toggle image:', error);
      throw error;
    }
  },

  /**
   * Bulk update display order of images
   * @param {Array} orderUpdates - Array of {id, display_order}
   * @returns {Promise<Object>} Success status
   */
  reorderImages: async (orderUpdates) => {
    try {
      return await apiService.put('/api/club-gallery/images/reorder', { order_updates: orderUpdates });
    } catch (error) {
      console.error('Failed to reorder images:', error);
      throw error;
    }
  },

  /**
   * Get carousel settings for the current club
   * @returns {Promise<Object>} Carousel settings
   */
  getSettings: async () => {
    try {
      const response = await apiService.get('/api/club-gallery/settings');
      // Backend returns settings object directly
      // Wrap it in data property for consistency
      return { data: response };
    } catch (error) {
      console.warn('Gallery settings API unavailable, using defaults:', error);
      // Fallback to defaults
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({ data: DUMMY_SETTINGS });
        }, 300);
      });
    }
  },

  /**
   * Update carousel settings
   * @param {Object} settings - Settings to update
   * @returns {Promise<Object>} Updated settings
   */
  updateSettings: async (settings) => {
    try {
      const response = await apiService.put('/api/club-gallery/settings', settings);
      // Backend returns updated settings directly
      // Wrap it in data property for consistency
      return { data: response };
    } catch (error) {
      console.error('Failed to update gallery settings:', error);
      throw error;
    }
  }
};

export default galleryApi;
